/**
 * Character Style Definitions
 *
 * Hardcoded style options for each medium/archetype.
 * Used by PromptBuilder to let users refine their character's visual style.
 *
 * Keywords are optimized for AI image generation models based on likely training data.
 */

import { CharacterArchetype } from '../types';

export interface StyleOption {
  id: string;
  name: string;
  description: string;
  previewColor: string;  // Fallback gradient/color when no image
  previewImage?: string; // Optional image URL for preview
  promptKeywords: string[];  // Keywords to add to generation prompt
}

export type StyleCategory = {
  archetype: CharacterArchetype;
  styles: StyleOption[];
};

// ============================================
// ANIME & MANGA STYLES
// ============================================

const ANIME_STYLES: StyleOption[] = [
  {
    id: 'ghibli',
    name: 'Ghibli',
    description: 'Soft curves, rounded faces, painterly warmth, gentle linework',
    previewColor: 'linear-gradient(135deg, #87CEEB 0%, #228B22 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/ghibli.jpg',
    promptKeywords: [
      'Studio Ghibli art style',
      'Hayao Miyazaki aesthetic',
      'Ghibli character design',
      'hand-painted animation aesthetic',
      'gouache painting technique',
      'watercolor anime coloring',
      'soft rounded character forms',
      'gentle curved outlines',
      'simple expressive features',
      'rounded face shapes',
      'dot or simple nose',
      'warm earthy color palette',
      'organic flowing linework',
      'anti-angular design philosophy',
      'hair thick strand rendering',
      'gentle movement suggestion',
      'painterly texture overlay',
      'traditional animation warmth',
      'nostalgic anime aesthetic',
      'humanist character design',
      'Ghibli color harmony',
      'studio_ghibli',
      '(miyazaki_hayao_(style):1.3)',
      'traditional_media',
      'gouache',
      'watercolor_(medium)',
      'masterpiece', 'best_quality'
    ]
  },
  {
    id: 'modern_clean_shonen',
    name: 'Digital Polish',
    description: 'Modern digital anime style, digital precision, cel-shaded polish',
    previewColor: 'linear-gradient(135deg, #00CED1 0%, #FF6347 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/modern_clean_shonen.jpg',
    promptKeywords: [
      'Koyoharu Gotouge art style',
      'Demon Slayer manga aesthetic',
      'Tatsuya Endo illustration',
      'Spy x Family visual style',
      'Kazue Kato Blue Exorcist rendering',
      'ufotable animation style',
      '2D 3D hybrid satsuei compositing',
      'Wit Studio CloverWorks co-production',
      'A-1 Pictures crisp visuals',
      'Clip Studio Paint digital workflow',
      'vector layer clean editable linework',
      'digital G-pen sharp anime inking',
      'pen pressure dynamics personal variation',
      'swift stroke clean line technique',
      '141 percent print size quality scaling',
      'layer-based non-destructive editing',
      '6-7 head toushin commercial proportions',
      'midpoint crotch wrist body division',
      'three-section torso construction',
      'cel-shading flat tone technique',
      'distinct shadow pattern layer structure',
      'multiply mode shadow application',
      'digital gradient airbrush integration',
      'manga gradient tool linear circular options',
      'screentone 20-60 LPI dot line texture',
      '600 dpi plus print-ready output',
      'clean defined hair sections',
      'chunky mass strand grouping',
      'clear silhouette character recognition',
      'digital gradient soft shapes',
      'airbrush gradation color rendering',
      'detailed eye construction technique',
      'top portion darkened eyelash shadow',
      'traditional Japanese ukiyo-e aesthetic fusion',
      '(demon_slayer_(style):1.2)',
      '(gotouge_koyoharu_(style):1.3)',
      '(ufotable_(style):1.2)',
      'anime_coloring',
      'digital_media_(artwork)',
      'masterpiece', 'best_quality',
      '2010s-2020s digital manga era'
    ]
  },
  {
    id: 'classic_battle_shonen',
    name: 'Classic Battle',
    description: 'Classic 90s battle manga style, bold G-pen linework, angular construction',
    previewColor: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/classic_battle_shonen.jpg',
    promptKeywords: [
      'Akira Toriyama art style',
      'Dragon Ball manga aesthetic',
      'Bird Studio illustration',
      'Weekly Shonen Jump house style',
      'Toei Animation character design',
      '1990s battle shonen aesthetic',
      'Zebra G-pen linework',
      'bold clean contour lines',
      'uniform line weight consistency',
      'thick-to-thin pressure variation',
      '6-7 head toushin proportions',
      'angular character construction',
      'minimal screentone shading',
      'solid black shadow fills',
      'flat cel-shading technique',
      'two-tone shadow rendering',
      'high-contrast composition',
      'white gap hair highlights technique',
      'bold eye outline rendering',
      'clean readable silhouette design',
      'nekketsu hot-blooded rendering',
      'primary color character logic',
      '(dragon_ball_(style):1.2)',
      '(toriyama_akira_(style):1.4)',
      'retro_art_style',
      '1990s_(style)',
      '(cel_shading:1.2)',
      '1980s-1990s shonen foundation'
    ]
  },
  {
    id: 'kodomomuke',
    name: 'Kids Adventure',
    description: 'Kids adventure anime style, bright colors, simple shapes, friendly characters',
    previewColor: 'linear-gradient(135deg, #FFD700 0%, #87CEEB 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/kodomomuke.jpg',
    promptKeywords: [
      'Pokemon anime art style',
      'Digimon Adventure character design',
      'Doraemon illustration style',
      'kodomomuke children anime',
      'large round eyes with big white highlights',
      'multiple catchlight reflections in eyes',
      'simple dot nose or small triangle nose',
      'bold uniform black outlines',
      'clean thick linework',
      'bright saturated colors',
      'flat cel shaded coloring',
      'simple single-tone shadows',
      'minimal color gradients',
      'clean color separation',
      'rounded simplified face shape',
      'youthful energetic character design',
      'simplified hand and finger construction',
      'creature mascot companion design',
      'instantly recognizable silhouette',
      'toy and merchandise ready design',
      'primary color palette emphasis',
      'high saturation vivid colors',
      'adventure anime optimistic mood',
      'Toei Animation style',
      'OLM anime aesthetic',
      '(pokemon_(style):1.2)',
      '(sugimori_ken_(style):1.3)',
      'flat_color',
      'outline',
      'late 1990s early 2000s anime style'
    ]
  },
  {
    id: 'shojo_nakayoshi',
    name: 'Magical Girl',
    description: 'Magical girl school style, maximum sparkle, transformation-ready design clarity',
    previewColor: 'linear-gradient(135deg, #FFB6C1 0%, #FF69B4 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/nakayoshi_sparkle.jpg',
    promptKeywords: [
      'Naoko Takeuchi art style',
      'CLAMP illustration technique',
      'Mia Ikumi manga aesthetic',
      'Nakayoshi magazine house style',
      '1990s magical girl aesthetic',
      'Toei Animation shojo style',
      'Madhouse Cardcaptor aesthetic',
      'thin curved line preference',
      'wispy magical stroke quality',
      'fluid free-flowing contours',
      'soft rounded world rendering',
      '5-6 head toushin proportions',
      'prolific screentone usage',
      'amiten dot patterns',
      'pastel color philosophy',
      'multiple highlight rendering',
      'soft gradient shading',
      '(cardcaptor_sakura_(style):1.2)',
      '(sailor_moon_(style):1.2)',
      'shoujo',
      'masterpiece',
      '1990s-2000s shojo peak era'
    ]
  },
  {
    id: 'gritty_sketchy_shonen',
    name: 'Gritty Sketch',
    description: 'Modern gritty shonen style, rough unfinished linework, heavy crosshatching',
    previewColor: 'linear-gradient(135deg, #3D3D3D 0%, #1A1A1A 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/gritty_sketchy_shonen.jpg',
    promptKeywords: [
      'Tatsuki Fujimoto art style',
      'Chainsaw Man manga aesthetic',
      'Gege Akutami illustration',
      'Jujutsu Kaisen visual style',
      'MAPPA animation source material',
      'Science SARU Dandadan aesthetic',
      'post-2016 rough shonen movement',
      'intentional unfinished linework',
      'jittery unstable contour lines',
      'visible pentimenti corrections',
      'sketch-like final rendering',
      'lines on verge of collapsing',
      'broken disconnected strokes',
      'energy gap line terminations',
      'thick-to-thin erratic variation',
      'kakeami Japanese cross-hatching',
      'overlapping hatching sets at angles',
      'Kentaro Miura hatching influence',
      'heavy beta-nori black areas',
      'high-contrast stark composition',
      'minimal gray value grunge quality',
      'rough textured screentone application',
      '6-7 head realistic toushin proportions',
      'reduced sparkle effects',
      'aggressive scratchy hatching technique',
      'raw energetic stroke quality',
      'psychological tension rendering',
      'modern dark shonen aesthetic',
      'Western oil painting manga fusion',
      '(chainsaw_man_(style):1.2)',
      '(jujutsu_kaisen_(style):1.2)',
      '(sketch:1.1)',
      'crosshatching',
      'rough_lines',
      'gekiga-influenced contemporary style'
    ]
  },
  {
    id: 'seinen_gekiga',
    name: 'Crime Noir',
    description: 'Sharp gritty realism, film noir visual influence, hyperrealistic anatomical precision',
    previewColor: 'linear-gradient(135deg, #2F2F2F 0%, #1a1a1a 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/neo_gekiga_crime.jpg',
    promptKeywords: [
      'Takao Saito art style',
      'Ryoichi Ikegami illustration',
      'Golgo 13 manga aesthetic',
      'Big Comic seinen style',
      'gekiga dramatic picture style',
      'neo-gekiga crime aesthetic',
      'sharp angle gekiga linework',
      'gritty realistic line quality',
      'film noir visual influence',
      'cinematic closeup technique',
      'character interiority expression',
      'hyper-realistic anatomical precision',
      'dramatic shading rendering',
      'lifelike proportion accuracy',
      'heavy kakeami hatching',
      'noir dramatic solid blacks',
      'high contrast atmospheric composition',
      'extensive screentone atmosphere',
      'hyperrealistic proportion philosophy',
      '(golgo_13_(style):1.2)',
      '(gekiga:1.3)',
      'hatching_(texture)',
      'realistic',
      'seinen',
      'masculine visual identity'
    ]
  },
  {
    id: 'jojo_baroque',
    name: 'Baroque Muscle',
    description: 'Sculptural anatomy, fashion illustration, Renaissance muscle rendering',
    previewColor: 'linear-gradient(135deg, #8B008B 0%, #FFD700 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/jojo.jpg',
    promptKeywords: [
      'Hirohiko Araki art style',
      'JoJo manga aesthetic',
      'JoJo\'s Bizarre Adventure style',
      'David Production anime design',
      'fashion illustration manga',
      'Versace aesthetic manga',
      'Renaissance sculpture anatomy',
      'Michelangelo influence',
      'fashion model proportions',
      'sculptural muscle definition',
      'dramatic anatomical exaggeration',
      'thick bold outlines',
      'heavy black ink areas',
      'airy negative space contrast',
      'Antonio Lopez fashion influence',
      'Tony Viramontes illustration',
      'classical sculpture reference',
      'stylized realistic anatomy',
      'bizarre fashion fusion',
      'Stand design aesthetic',
      '(jojo_no_kimyou_na_bouken_(style):1.3)',
      '(araki_hirohiko_(style):1.4)',
      'muscular',
      'jo_jo_pose',
      'masterpiece',
      'color shift palette technique'
    ]
  },
  {
    id: 'trigger',
    name: 'Explosive Action',
    description: 'Explosive action anime style, exaggerated anatomy, maximum energy, thick outlines',
    previewColor: 'linear-gradient(135deg, #FF0000 0%, #000000 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/trigger.jpg',
    promptKeywords: [
      'Studio Trigger art style',
      'Hiroyuki Imaishi aesthetic',
      'Kill la Kill visual style',
      'Promare illustration',
      'Sushio character design',
      'explosive anime action',
      'thick bold outlines',
      'heavy line weight variation',
      'extreme perspective distortion',
      'dramatic foreshortening',
      'rubbery anatomy exaggeration',
      'neo-Kanada animation style',
      'hot-blooded nekketsu aesthetic',
      'over-the-top rendering',
      'red black color scheme',
      'dramatic silhouette design',
      'explosive energy rendering',
      'rule of cool anatomy',
      'maximum expression illustration',
      'Gainax legacy aesthetic',
      'webgen animation influence',
      'kinetic maximum energy',
      '(studio_trigger:1.3)',
      '(kill_la_kill_(style):1.2)',
      '(imaishi_hiroyuki_(style):1.3)',
      'promare_(style)',
      'coiled motion stillness'
    ]
  },
  {
    id: 'dark_ova',
    name: 'Dark OVA',
    description: '90s dark fantasy OVA style, sharp angular anatomy, analog film aesthetic, high contrast cel shading',
    previewColor: 'linear-gradient(135deg, #1a0a20 0%, #2d1f3d 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/dark_ova.jpg',
    promptKeywords: [
      '90s Dark OVA style',
      'late 80s anime aesthetic',
      'retro dark fantasy anime',
      '1990s cel-shaded animation',
      'art by Yoshiaki Kawajiri',
      'Madhouse 1990s style',
      'Wicked City 1987 aesthetic',
      'Ninja Scroll 1993 rendering',
      'Cyber City Oedo 808 style',
      '8-toushin proportions',
      'elongated slender anatomy',
      'sharp facial angles',
      'elegant sharp silhouettes',
      'sharp needle-thin lineart',
      'variable G-pen strokes',
      'tapered ink lines',
      'visible linework texture',
      'unrefined analog line weight',
      'hard-edge cel shading',
      'posterized two-tone shadows',
      'high-contrast chiaroscuro',
      'mechanical halftone screentone',
      'dithered shading gradients',
      'cross-hatch depth',
      'high value contrast',
      'saturated black shadows',
      'matte paint texture',
      'scanned animation cel aesthetic',
      '35mm film grain',
      'analog noise',
      'chromatic aberration',
      'low-fidelity film stock',
      'VHS tracking artifacts',
      'telecine wobble',
      'rim lighting',
      '(kawajiri_yoshiaki_(style):1.3)',
      '(ninja_scroll_(style):1.2)',
      'retro_artstyle',
      '1980s_(style)',
      '1990s_(style)',
      'dark_atmosphere',
      'mature',
      'masterpiece',
      // Dark OVA characteristic coloring
      'muted desaturated color palette',
      'subdued earthy tones',
      'limited atmospheric color scheme',
      'gouache paint color aesthetic',
      'hand-painted cel coloring',
      'soft diffused lighting',
      'painterly color wash',
      'analog color grading',
      'faded vintage color saturation'
    ]
  },
  {
    id: 'trigger_a1_collab',
    name: 'Graphic Glamour',
    description: 'Trigger x A-1 collaboration style, flat color blocking, bold graphic composition, glamorous character appeal',
    previewColor: 'linear-gradient(135deg, #FF4D6D 0%, #00B4D8 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/graphic_glamour.jpg',
    promptKeywords: [
      'Masayoshi Tanaka character design',
      'Studio Trigger animation style',
      'A-1 Pictures production quality',
      'Darling in the Franxx anime aesthetic',
      'modern anime-original mecha style',
      'Trigger x A-1 collaboration aesthetic',
      '2010s anime-original production',
      'clean economical linework',
      'animation-efficient character design',
      'simplified reductivist design philosophy',
      'flat color blocking technique',
      'minimal internal shading',
      'bold color mass rendering',
      'high saturation complementary palette',
      'vibrant punchy color philosophy',
      'seaweed-clump hair rendering',
      'hair as bold shape masses',
      'hair volume flutter movement',
      'distinctive Tanaka eye design',
      'almond-shaped gradient iris eyes',
      'sharp angular accents on soft faces',
      'glamorous bewitching character appeal',
      'poster-like graphic composition',
      'graphic design clarity',
      'smooth digital coloring',
      'cel-shaded flat aesthetic',
      'bold character silhouette priority',
      'GAINAX heritage visual language',
      'ex-GAINAX staff aesthetic',
      'Hiroyuki Imaishi action influence',
      'Shigeto Koyama mechanical design',
      '2010s late-night anime aesthetic',
      'anime-original production values',
      'darling_in_the_franxx_(style)',
      '(tanaka_masayoshi_(style):1.2)',
      'flat_color',
      'vibrant_colors',
      'best_quality',
      'streaming era high-polish finish'
    ]
  },
  {
    id: 'clamp',
    name: 'Elongated Elegance',
    description: 'Extreme vertical elongation, impossibly long limbs and necks',
    previewColor: 'linear-gradient(135deg, #9370DB 0%, #4B0082 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/clamp.jpg',
    promptKeywords: [
      'CLAMP manga style',
      'Mokona art style',
      'xxxHolic proportions',
      'elongated limbs aesthetic',
      '10-head tall proportions',
      'extreme vertical stretch',
      'noodle people anatomy',
      'impossibly long fingers',
      'elongated neck rendering',
      'felt tip pen linework',
      'even pressure strokes',
      'no screentone technique',
      'negative space composition',
      'calligraphic body rendering',
      'graceful impossible anatomy',
      'dramatic silhouette design',
      'ethereal flowing fabric rendering',
      '(clamp_(style):1.4)',
      'xxxholic_(style)',
      'tall',
      'long_legs',
      'CLAMP universe aesthetic'
    ]
  },
  {
    id: 'seinen_miura',
    name: 'Dark Fantasy',
    description: 'Obsessive detail maximalism, multi-directional crosshatching, engineering-level precision',
    previewColor: 'linear-gradient(135deg, #1a1a1a 0%, #3d3d3d 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/miura_school.jpg',
    promptKeywords: [
      'Kentaro Miura art style',
      'Hiroaki Samura illustration',
      'Makoto Yukimura manga aesthetic',
      'Young Animal seinen style',
      'dark fantasy manga aesthetic',
      'traditional pen-and-ink technique',
      'G-pen primary linework',
      'mechanical pencil hybrid shading',
      'obsessive minute crosshatching',
      'multi-directional layered hatching',
      'Gustave Doré printmaker influence',
      'Albrecht Dürer engraving technique',
      '7-8 head toushin realistic proportions',
      'technical drawing precision',
      'minimal screentone preference',
      'hand-drawn texture density',
      'progressive hatching buildup',
      'heavy beta-nuri dramatic contrast',
      'white linework within black areas',
      'Baroque period manga influence',
      '(berserk_(style):1.3)',
      '(miura_kentarou_(style):1.4)',
      '(hatching:1.2)',
      'detailed',
      'masterpiece',
      'Western fine art crossover',
      // Dark fantasy coloring
      'full color illustration',
      'dark earthy color palette',
      'muted browns and deep reds',
      'desaturated gritty tones',
      'oil painting color aesthetic',
      'Frazetta color influence',
      'grimdark color grading',
      'muddy earth tones',
      'dramatic chiaroscuro lighting'
    ]
  },
  {
    id: 'moe_kawaii',
    name: 'Cute Kawaii',
    description: 'Cute protective style, large low-set eyes, soft rounded forms',
    previewColor: 'linear-gradient(135deg, #FFB6C1 0%, #DDA0DD 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/moe.jpg',
    promptKeywords: [
      'moe anime style',
      'Kyoto Animation house style',
      'KyoAni aesthetic',
      'K-On art style',
      'cute anime design',
      'moeblob character style',
      'Yukiko Horiguchi design',
      'edgeless line-tracing technique',
      'large low-set eyes',
      'glossy eyes multiple highlights',
      '2-3 reflection points per eye',
      'soft rounded face shape',
      'small button nose',
      'tiny minimal mouth',
      'puni plush soft rendering',
      'pastel color palette',
      'gentle gradient shading',
      'fine delicate linework',
      '3-4 head tall proportions',
      'oversized head ratio',
      'soft curved hair shapes',
      'youthful neotenous features',
      'warm gentle aesthetic',
      'kirara-kei slice-of-life',
      'nichijou-kei daily life',
      'soft cel shading',
      'k-on!_(style)',
      '(kyoto_animation:1.3)',
      '(horiguchi_yukiko_(style):1.2)',
      'moe',
      'best_quality',
      'rounded corner design philosophy'
    ]
  },
  {
    id: 'sports_shonen',
    name: 'Clean Realism',
    description: 'Sports manga style, realistic athletic proportions, ink brush technique',
    previewColor: 'linear-gradient(135deg, #FF4500 0%, #1E90FF 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/sports_shonen.jpg',
    promptKeywords: [
      'Takehiko Inoue art style',
      'Slam Dunk manga aesthetic',
      'Haruichi Furudate illustration',
      'Haikyuu visual style',
      'Production I.G sports anime',
      'summarizing realism design philosophy',
      'G-pen authoritative weighted strokes',
      'variable thickness following muscle curves',
      'economic stroke efficiency',
      'minimal strokes conveying form',
      'dip pen subtle delicate work',
      'brush drawing expressive freedom',
      'sumi Japanese ink technique',
      'dry brush texture effects',
      '7-8 head toushin proportions',
      'dot screentone gradient shading',
      'density variation LPI light-to-dark transitions',
      'gradient screentone anatomical structure',
      'muscle group shadow rendering',
      'economic brushwork hair rendering',
      '(slam_dunk_(style):1.2)',
      '(haikyuu!!_(style):1.2)',
      '(inoue_takehiko_(style):1.3)',
      '1990s sports shonen foundation',
      'classical Japanese ink painting influence'
    ]
  },
  {
    id: 'late_night_action',
    name: 'Late-Night Action',
    description: 'Mature seinen action style, realistic anatomy, ecchi action aesthetic, high production values',
    previewColor: 'linear-gradient(135deg, #8B0000 0%, #2F2F2F 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/late_night.jpg',
    promptKeywords: [
      'Shoji Sato art style',
      'Kentaro Yabuki illustration',
      'Highschool of the Dead manga aesthetic',
      'Monthly Dragon Age seinen style',
      'late-night anime aesthetic',
      '2000s ecchi action style',
      'Madhouse action adaptation',
      'Xebec digital animation style',
      'Arms Corporation polish',
      'TNK dramatic lighting',
      'clean digital linework',
      'G-pen precise inking',
      'sharp decisive strokes',
      'thick-to-thin line weight hierarchy',
      'maru-pen fine detail work',
      '7-8 head toushin proportions',
      'mature anatomical rendering',
      'realistic tissue physics',
      '50-60 LPI screentone density',
      'high-contrast beta-nuri shading',
      'tsuya-beta glossy hair rendering',
      'white highlight within black hair technique',
      'saturated cel-shading',
      'hard-edge shadow shapes',
      'discrete tonal steps',
      'warm character palette against cool backgrounds',
      'strong directional lighting',
      'rim light character separation',
      'soft character rendering against precise mechanical elements',
      'technical equipment precision',
      'photorealistic background reference',
      'doujinshi-to-commercial pipeline aesthetic',
      'Comiket talent pool influence',
      'visual novel character design influence',
      'moe eyes on mature frame contrast',
      'highschool_of_the_dead_(style)',
      '(sato_shouji_(style):1.2)',
      'ecchi',
      'shiny_skin',
      '2005-2015 late-night anime peak era',
      'DVD Blu-ray production values',
      'monthly serialization detail density'
    ]
  },
  {
    id: 'shojo_gothic',
    name: 'Dark Romance',
    description: 'Hana to Yume style, dramatic contrast, heavy G-pen, layered screentone atmosphere',
    previewColor: 'linear-gradient(135deg, #4B0082 0%, #8B0000 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/gothic_drama.jpg',
    promptKeywords: [
      'Kaori Yuki art style',
      'Matsuri Hino illustration',
      'Natsuki Takaya manga aesthetic',
      'Hana to Yume magazine style',
      'Studio Deen dark shojo style',
      'heavy G-pen bold linework',
      'maru-pen intricate detailing',
      'brush pen dramatic strokes',
      'strong thick-to-thin line variation',
      '7-8 head toushin proportions',
      'heavy layered screentone atmosphere',
      'high LPI frequency texturing',
      'extensive kakeami crosshatching',
      'liberal beta-nuri solid blacks',
      'high contrast chiaroscuro composition',
      'deep rich color tones',
      'crimson red midnight blue palette',
      'dramatic tonal opposition',
      '(vampire_knight_(style):1.2)',
      '(hino_matsuri_(style):1.3)',
      'gothic',
      'dark_atmosphere',
      '1990s-2000s gothic shojo era'
    ]
  },
  {
    id: 'seinen_otomo',
    name: 'Neo Cyberpunk',
    description: 'Architectural precision, no cross-hatching, line weight creates value, megastructure rendering',
    previewColor: 'linear-gradient(135deg, #708090 0%, #2F4F4F 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/otomo_school.jpg',
    promptKeywords: [
      'Katsuhiro Otomo art style',
      'Masamune Shirow illustration',
      'Tsutomu Nihei manga aesthetic',
      'Young Magazine cyberpunk style',
      '1980s New Wave manga movement',
      'architectural precision rendering',
      'G-pen and maru-pen technical linework',
      'NO cross-hatching technique',
      'line weight value variation',
      'line frequency tonal control',
      'dense purposeful environment detail',
      'directional hatching only',
      '7-8 head toushin proportions',
      'spartan strategic screentone',
      'three-tone black white gray approach',
      'minimal solid black compositional shapes',
      'Moebius Jean Giraud influence',
      'ligne claire clear line technique',
      'bleak vast environment rendering',
      '(akira_(style):1.3)',
      '(otomo_katsuhiro_(style):1.4)',
      'cyberpunk',
      '1980s-2000s cyberpunk aesthetic'
    ]
  },
  {
    id: 'chibi',
    name: 'Mini / SD',
    description: 'Super deformed 2-3 head ratio, oversized head, stub limbs',
    previewColor: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/chibis.jpg',
    promptKeywords: [
      'chibi anime style',
      'super deformed SD style',
      'Nendoroid figure aesthetic',
      'deforume Japanese style',
      '2-head proportion ratio',
      '3-head tall maximum',
      'oversized spherical head',
      'tiny stubby body',
      'vestigial stub limbs',
      'simplified dot eyes',
      'minimal facial features',
      'round circular face',
      'compact torso design',
      'mitten hands simplified',
      'stubby leg proportions',
      'simplified hair shape',
      'bold outline emphasis',
      'flat color rendering',
      'minimal shading',
      'mascot character proportions',
      'Good Smile Company style',
      'Q Posket aesthetic',
      'emotional shorthand design',
      'comedic proportion distortion',
      'merchandise-optimized design',
      '(chibi:1.2)',
      'super_deformed',
      'cute',
      'kawaii figure illustration'
    ]
  },
  {
    id: 'comedy_gag_shonen',
    name: 'Round Soft',
    description: 'Comedy gag manga style, elastic expressions, super-deformed proportions',
    previewColor: 'linear-gradient(135deg, #FFE135 0%, #FF6B6B 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/comedy_gag_shonen.jpg',
    promptKeywords: [
      'Akira Toriyama gag manga style',
      'Dr. Slump aesthetic',
      'Hideaki Sorachi illustration',
      'Gintama visual style',
      'Sunrise BN Pictures comedy anime',
      'J.C.Staff distinctive style',
      'Lerche comedic anime production',
      'Osamu Tezuka Disney influence',
      '1980s shonen gag manga foundation',
      'G-pen and marker technique',
      'calligraphy brush pen strokes',
      'School pen Spoon pen uniform lines',
      'consistent line weight sen-fuku',
      'clean unbroken sumisen outlines',
      'Milli Pen cartoony uniform thickness',
      '2-head toushin chibi proportions',
      '3-head common comedic proportion',
      '4-head small child proportion',
      '6-7 head normal proportion baseline',
      'super-deformed SD proportion switching',
      'proportion shift comedic timing',
      'minimal to no shading philosophy',
      'flat coloring high-contrast approach',
      'simple screentone 50-60 LPI halftone',
      'minimal screentone clean visual comedy',
      'simple interlocking hair clump masses',
      'sculpted solid hair object rendering',
      'single confident stroke clump definition',
      'simplified feature rendering',
      'Bambi Disney rounded aesthetic absorption',
      'nichijou_(style)',
      '(gintama_(style):1.2)',
      'Tezuka large eye convention foundation'
    ]
  },
  {
    id: 'webgen_fluid',
    name: 'Web-Gen',
    description: 'Sakuga animation style, motion over consistency, dynamic distortion',
    previewColor: 'linear-gradient(135deg, #FF4500 0%, #FFD700 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/web-gen.jpg',
    promptKeywords: [
      'sakuga animation style',
      'Yutaka Nakamura style',
      'yutapon aesthetic',
      'key animation genga style',
      'web-gen animator influence',
      'fluid animation frame',
      'stretched anatomy for motion',
      'off-model expressive drawing',
      'loose energetic linework',
      'thick thin line variation',
      'liquid movement rendering',
      'neo-Kanada animation style',
      'full limited animation',
      'personal animator expression',
      'Studio Bones sakuga',
      'exaggerated squash stretch',
      'kinetic energy rendering',
      'frame-by-frame expression',
      'action peak illustration',
      'dynamic line weight',
      'movement over consistency',
      'raw animation energy',
      '(sakuga:1.3)',
      'webgen',
      'douga in-between aesthetic'
    ]
  },
  {
    id: 'clean_digital',
    name: 'Digital',
    description: 'Manhwa/webtoon style, vector-like lines, airbrushed shading',
    previewColor: 'linear-gradient(135deg, #4169E1 0%, #9400D3 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/digital.jpg',
    promptKeywords: [
      'Korean manhwa style',
      'Solo Leveling art style',
      'DUBU illustration style',
      'webtoon digital art',
      'Redice Studio aesthetic',
      'clean vector linework',
      'smooth digital lines',
      'airbrushed gradient shading',
      'cell-shaded coloring',
      'full color webtoon',
      'sharp facial geometry',
      'idol character proportions',
      'elongated sharp features',
      'high contrast value shading',
      'complementary color shadows',
      'smooth tonal transitions',
      'vertical scroll composition',
      'panel-less flowing layout',
      'KakaoPage webtoon style',
      'Naver Webtoon aesthetic',
      'LINE Webtoon format',
      'pristine digital rendering',
      'no visible brush texture',
      'gradient color backgrounds',
      'fantasy manhwa aesthetic',
      '2020s digital comic style',
      'mobile-optimized illustration',
      '(solo_leveling_(style):1.2)',
      '(manhwa:1.2)',
      'webtoon',
      'full_color',
      'best_quality',
      'cinematic webtoon rendering'
    ]
  },
  {
    id: 'josei_yazawa',
    name: 'Fashion Manga',
    description: 'Yazawa school, fashion illustration proportions, confident runway-quality linework',
    previewColor: 'linear-gradient(135deg, #8B7355 0%, #A0522D 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/fashion_editorial.jpg',
    promptKeywords: [
      'Ai Yazawa art style',
      'Moyoco Anno illustration',
      'Cookie magazine josei style',
      'Madhouse josei adaptation style',
      'traditional manga inking',
      'confident bold linework',
      'impeccable clean strokes',
      '9-11 head toushin elongated proportions',
      'tall slender body ratios',
      'strategic dynamic screentoning',
      'high contrast drama',
      '(nana_(style):1.3)',
      '(yazawa_ai_(style):1.4)',
      'josei',
      'fashion',
      'long_eyelashes',
      'masterpiece',
      '2000s josei manga aesthetic'
    ]
  },
  {
    id: 'moe_adjacent_shonen',
    name: 'Cute Romance',
    description: 'Romantic comedy anime style, soft gradients, bishoujo hair rendering',
    previewColor: 'linear-gradient(135deg, #FFB6C1 0%, #87CEEB 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/moe_adjacent_shonen.jpg',
    promptKeywords: [
      'Kentaro Yabuki art style',
      'To Love-Ru manga aesthetic',
      'Naoshi Komi illustration',
      'Nisekoi visual style',
      'Negi Haruba Quintessential Quintuplets rendering',
      'Xebec romantic comedy animation',
      'Shaft Akiyuki Shinbo style',
      'Shaft head-tilt extreme angles',
      'minimalist background sharp color contrast',
      'Bibury Animation Studios bright character design',
      'Clip Studio Paint Photoshop digital workflow',
      'clean polished consistent linework',
      'thin delicate line weights',
      'smooth minimal rough elements',
      'soft flowing curve emphasis',
      'graceful continuous lines',
      'minimal harsh angle softened corners',
      'long fluid stroke preference',
      'soft rendering technique reference',
      '5-6 head toushin youthful proportions',
      'larger head ratio approachable aesthetic',
      'eye below horizontal halfway head position',
      'soft gradient shading approach',
      'airbrush style soft transitions',
      'multiply layer pale pink blue shadow',
      'clipping mask clean separation workflow',
      'color dodge white glow highlights',
      'gradient tone natural light screentone',
      '10-70 percent density range',
      'bishoujo detailed flowing hair conventions',
      'high strand detail chunky section grouping',
      'multiple layer highlight rendering',
      'primary crown highlight brightest',
      'secondary flow direction highlight',
      'ring-shaped angel ring tenshi no wa shine',
      'skin color airbrush transparent bangs layering',
      'large moe eye rendering',
      'multiple reflection points main and ambient',
      'top darkened bottom lighter eye gradient',
      'light novel illustration color sensibility',
      'bishoujo visual novel game art influence',
      '2000s moe revolution K-On Lucky Star aesthetic absorption',
      'cross-demographic soft appeal design',
      'nisekoi_(style)',
      '(shaft_(style):1.2)',
      '(komi_naoshi_(style):1.2)',
      'shiny_hair'
    ]
  },
  {
    id: 'shojo_otome',
    name: 'Otome Romance',
    description: 'Clean controlled linework, strategic screentone, vibrant coloring, period detail attention',
    previewColor: 'linear-gradient(135deg, #FFC0CB 0%, #FF69B4 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/otome_visual_novel.jpg',
    promptKeywords: [
      'Mizuho Kusanagi art style',
      'Julietta Suzuki illustration',
      'Hana to Yume otome aesthetic',
      'visual novel influenced manga style',
      'Studio Pierrot shojo adaptation',
      'TMS Entertainment romantic fantasy',
      'maru-pen fine clean lines',
      'controlled line intensity variation',
      'dissolving background technique',
      '6-7 head female toushin proportions',
      '7-8 head male toushin proportions',
      'moderate strategic screentone application',
      '40-60 LPI standard frequency',
      'soft romantic color scheme',
      'pretty vibrant coloring',
      '2010s otome game aesthetic influence',
      'otome_game',
      'amnesia_(style)',
      '(ikemen:1.1)',
      'bishounen',
      'period detail rendering',
      'multiple character silhouette clarity'
    ]
  },
  {
    id: 'horror_manga',
    name: 'Horror',
    description: 'Horror manga style, generic faces violated by hyper-detail, stippling',
    previewColor: 'linear-gradient(135deg, #1a1a1a 0%, #8B0000 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/horror.jpg',
    promptKeywords: [
      'Junji Ito manga style',
      'Uzumaki horror aesthetic',
      'Japanese horror manga illustration',
      'body horror rendering',
      'kakeami crosshatching horror',
      'meticulous detail grotesque',
      'high contrast rendering',
      'fine line stippling',
      'hyper-detailed grotesquerie',
      'simple character design contrast',
      'generic face horror contrast',
      'pallid lifeless face rendering',
      'detailed fear eye rendering',
      'open edge attractive outlines',
      'dense horror crosshatching',
      'impossible anatomy horror',
      'uncanny valley rendering',
      'cosmic horror illustration',
      'psychological horror manga',
      'ordinary-to-horrifying transition',
      'obsessive fine linework',
      'textural wrongness rendering',
      'Lovecraftian influence manga',
      'body contortion horror',
      '(itou_junji_(style):1.4)',
      '(uzumaki_(style):1.2)',
      'horror',
      'creepy',
      'grotesque',
      'escalating detail density'
    ]
  },
  {
    id: 'shojo_lala',
    name: 'Soft Elegance',
    description: 'Bishonen focus, delicate refined linework, atmospheric screentone, tranquil nostalgic mood',
    previewColor: 'linear-gradient(135deg, #E6E6FA 0%, #D8BFD8 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/lala_elegance.jpg',
    promptKeywords: [
      'Bisco Hatori art style',
      'Yuki Midorikawa illustration',
      'Yoshiki Nakamura manga aesthetic',
      'LaLa magazine house style',
      'elegant shojo aesthetic',
      'Brain\'s Base anime style',
      'Bones shojo adaptation aesthetic',
      'maru-pen delicate linework',
      'thin fragile stroke quality',
      'rough pen line texture',
      'dissolving background line technique',
      '7-8 head toushin proportions',
      'atmospheric screentone integration',
      'fuzzy black dramatic effect technique',
      'minimal background rendering',
      'soft subdued color palette',
      'tranquil nostalgic color mood',
      '2000s-2020s refined shojo era',
      'emotional expression focus',
      'elegant proportion philosophy',
      'sophisticated composition approach',
      '(ouran_high_school_host_club_(style):1.2)',
      '(hatori_bisco_(style):1.3)',
      'shoujo'
    ]
  },
  {
    id: 'iyashikei',
    name: 'Gentle Healing',
    description: 'Healing style, maximum gentleness, soft watercolor, white space',
    previewColor: 'linear-gradient(135deg, #98FB98 0%, #F0FFF0 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/iyashikei.jpg',
    promptKeywords: [
      'iyashikei manga style',
      'healing anime aesthetic',
      'Kozue Amano art style',
      'Aria manga illustration',
      'Mushishi anime style',
      'soft watercolor technique',
      'gentle line weight',
      'thin delicate outlines',
      'muted color palette',
      'soft blue green tones',
      'earthy warm colors',
      'soft gradient shading',
      'generous white space',
      'minimal value contrast',
      'peaceful contemplative mood',
      'mono no aware aesthetic',
      'gentle character design',
      'rounded soft features',
      'minimal dramatic tension',
      'soothing color gradation',
      'restrained emotional rendering',
      'warm nostalgic tone',
      '(yuru_camp_(style):1.2)',
      '(aria_(style):1.2)',
      '(amano_kozue_(style):1.3)',
      'watercolor_(medium)',
      'pastel_colors',
      'subtle ink wash technique'
    ]
  },
  {
    id: 'tezuka',
    name: 'Retro Foundational',
    description: '1960s foundational anime style, Disney-influenced curves, large circular eyes',
    previewColor: 'linear-gradient(135deg, #2196F3 0%, #FF9800 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/tezuka.jpg',
    promptKeywords: [
      'Osamu Tezuka manga style',
      'Astro Boy aesthetic',
      'Mushi Production design',
      '1960s manga style',
      'God of Manga influence',
      'large circular expressive eyes',
      'Disney-influenced anime',
      'Takarazuka Revue eye style',
      'small minimal mouth',
      'neotenous facial features',
      'rounded soft character forms',
      'simplified nose dot or line',
      'oversized head proportions',
      'gentle curved outlines',
      'clean uniform line weight',
      'minimal crosshatching',
      'flat cel-style shading',
      'black spot shadows',
      'star system character design',
      'warm humanist character rendering',
      'rubber-hose evolved limbs',
      'simple hand construction',
      'round fingertips',
      'soft curved hair shapes',
      '1950s manga foundation',
      '(tezuka_osamu_(style):1.4)',
      'astro_boy_(style)',
      'retro_art_style',
      'monochrome',
      'pioneering anime aesthetic',
      'Tezuka Productions style'
    ]
  },
  {
    id: 'kawajiri',
    name: 'Gothic OVA',
    description: 'Dark fantasy OVA style, gothic elongated anatomy, ultra-detailed linework',
    previewColor: 'linear-gradient(135deg, #2C1810 0%, #4A0E0E 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/kawajiri.jpg',
    promptKeywords: [
      'Yoshiaki Kawajiri animation style',
      'Yoshitaka Amano-inspired character base',
      'Studio Madhouse early-2000s production quality',
      'hyper-detailed technical linework',
      'ultra-fine razor-sharp line weights',
      'hand-painted physical cel texture',
      'elongated anatomical proportions',
      'flowing liquid-like hair rendering',
      'Chromegoth material design',
      'matte leather and polished metal textures',
      'cinematic 35mm film grain',
      'early 2000s high-fidelity OVA aesthetic',
      'dark science-fantasy atmosphere',
      'vampire_hunter_d_(style)',
      '(kawajiri_yoshiaki_(style):1.4)',
      'ninja_scroll_(style)',
      'gothic',
      'sinister macabre detailing'
    ]
  },
  {
    id: 'seinen_urasawa',
    name: 'Thriller Realism',
    description: 'Psychological realism, invisible technical perfection, millimeter-precision expression',
    previewColor: 'linear-gradient(135deg, #4A4A4A 0%, #2C2C2C 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/urasawa_school.jpg',
    promptKeywords: [
      'Naoki Urasawa art style',
      'Monster manga aesthetic',
      'Big Comic Original seinen style',
      'psychological thriller manga aesthetic',
      'Madhouse anime adaptation style',
      'controlled consistent line weights',
      'invisible perfection technique',
      'millimeter-level expression precision',
      'realistic human proportions',
      'minimal cartoonish exaggeration',
      'prominent atmospheric screentone usage',
      'strategic bold black-white panels',
      'restrained selective hatching',
      'gekiga sharp angle influence',
      'gritty realistic line quality',
      'cinematic storyboard composition',
      'expert panel arrangement',
      'visual economy philosophy',
      'Katsuhiro Otomo style alignment',
      '1990s-2000s psychological seinen era'
    ]
  },
  {
    id: 'josei_umino',
    name: 'Cozy Watercolor',
    description: 'Umino school, soft organic linework, watercolor wash, domestic warmth',
    previewColor: 'linear-gradient(135deg, #DEB887 0%, #F5DEB3 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/warm_watercolor.jpg',
    promptKeywords: [
      'Chica Umino art style',
      'Honey and Clover manga aesthetic',
      'March Comes in Like a Lion visual style',
      'Young Animal josei crossover',
      'J.C.Staff anime adaptation style',
      'Shaft emotional rendering technique',
      'simple soft line quality',
      'organic gentle stroke character',
      'emotional warmth linework',
      '6-7 head toushin approachable proportions',
      'plain realistic character design',
      'watercolor wash effect simulation',
      'soft gradient shading',
      'radiating domestic warmth rendering',
      'warm earth tone emphasis',
      'vibrant appealing color palette',
      'emotional contrast rendering',
      '(honey_and_clover_(style):1.2)',
      '(umino_chica_(style):1.3)',
      'watercolor_(style)',
      'warm_colors',
      'cozy inviting domestic space rendering',
      '2000s-2010s warm josei era'
    ]
  },
  {
    id: 'shojo_ribon',
    name: 'Soft Romcom',
    description: 'Romantic comedy style, soft rounded forms, minimal shading, warm everyday aesthetic',
    previewColor: 'linear-gradient(135deg, #FFDAB9 0%, #FFB6C1 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/ribon_soft_round.jpg',
    promptKeywords: [
      'Wataru Yoshizumi art style',
      'Miho Obana illustration',
      'Ribon magazine house style',
      '1990s shojo romantic comedy aesthetic',
      'Toei Animation rom-com style',
      'Studio Gallop shojo aesthetic',
      'maru-pen light linework',
      'uniform line weight consistency',
      'soft dissolving stroke quality',
      'rounded form emphasis',
      'minimal angular construction',
      '5-6 head toushin proportions',
      'light sparse screentone usage',
      'low LPI frequency gradation',
      'minimal beta-nuri black fills',
      'airy open composition',
      'soft pastel color tendency',
      'high-key tonal range',
      'marmalade_boy_(style)',
      '(yoshizumi_wataru_(style):1.2)',
      'shoujo',
      'soft_coloring',
      'hand-drawn aesthetic emphasis',
      'comedic proportion flexibility',
      'SD chibi mode capability',
      'warm approachable rendering'
    ]
  },
  {
    id: 'shojo_year24',
    name: 'Classic Shojo',
    description: '1970s revolution style, borderless flowing panels, psychological interiority, sparse elegant linework',
    previewColor: 'linear-gradient(135deg, #DDA0DD 0%, #9370DB 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/year_24.jpg',
    promptKeywords: [
      'Moto Hagio art style',
      'Riyoko Ikeda illustration',
      'Year 24 Group manga aesthetic',
      '1970s shojo revolution style',
      'crow quill pen linework',
      'maru-pen delicate strokes',
      'India ink traditional technique',
      'sparse elegant line quality',
      'flowing minimal hatching',
      'high contrast composition',
      'stark tonal opposition',
      'minimal screentone application',
      'kakeami bundled-line crosshatching',
      'strategic beta-nuri black fills',
      '6-8 head toushin proportions',
      'floating figure layout',
      'European watercolor influence',
      'psychological interiority rendering',
      '(rose_of_versailles_(style):1.3)',
      '(ikeda_riyoko_(style):1.4)',
      'shoujo',
      'retro_art_style',
      'symbolic visual language',
      'sparse line economy'
    ]
  },
  {
    id: 'shojo_betsuma',
    name: 'Mature Romance',
    description: 'Mature romance, naturalistic proportions, analog tactile technique, watercolor nostalgia',
    previewColor: 'linear-gradient(135deg, #87CEEB 0%, #B0E0E6 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/betsuma_naturalism.jpg',
    promptKeywords: [
      'Io Sakisaka art style',
      'Yuki Obata illustration',
      'Bessatsu Margaret magazine style',
      'mature shojo romance aesthetic',
      'Production I.G shojo adaptation',
      'traditional analog pen technique',
      'tactile paper texture quality',
      'soft flowing contour lines',
      'emotional depth linework',
      '6-7 head toushin naturalistic proportions',
      'light screentone tender shading',
      'Delieta tone technique',
      'watercolor memory sequence rendering',
      'soft pastel cool tone palette',
      'bright airy coloring',
      'pale emotional distance rendering',
      '2000s-2010s naturalistic shojo era',
      'vertical panel emotional pacing',
      'atmospheric blur technique',
      'understated expression philosophy',
      'sakisaka_io_(style)',
      'bessatsu_margaret',
      'shoujo'
    ]
  },
  {
    id: 'josei_bl',
    name: 'Boys Love',
    description: 'Boys Love refined style, delicate linework, romantic elegance',
    previewColor: 'linear-gradient(135deg, #9370DB 0%, #BA55D3 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/bl_refined.jpg',
    promptKeywords: [
      'Natsuki Kizu art style',
      'Rihito Takarai illustration',
      'Shungiku Nakamura manga aesthetic',
      'Cheri+ magazine BL style',
      'Be x Boy refined BL aesthetic',
      'Dear+ josei BL style',
      'refined delicate line work',
      'clean fluid stroke quality',
      'soft drawing style',
      'delicate beautiful illustration',
      '7-8 head toushin bishonen proportions',
      'beauty and sensuality emphasis',
      'fragility ephemerality softness',
      'standard dot screentone light-medium density',
      '10-40 percent tonal range',
      'minimal crosshatching clean areas',
      'conservative solid black usage',
      'strategic dramatic black fills',
      '2000s-2020s BL refinement era',
      '(shounen_ai:1.1)',
      'bishounen',
      '(takarai_rihito_(style):1.2)',
      'boys_love'
    ]
  },
  {
    id: 'gothic_shonen',
    name: 'Intricate Gothic',
    description: 'Gothic fantasy style, intricate linework, bishōnen elegance',
    previewColor: 'linear-gradient(135deg, #2C1654 0%, #0D0D0D 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/gothic_shonen.jpg',
    promptKeywords: [
      'Katsura Hoshino art style',
      'D.Gray-man manga aesthetic',
      'Jun Mochizuki illustration',
      'Vanitas no Carte visual style',
      'Yana Toboso Black Butler rendering',
      'Monthly GFantasy magazine style',
      'TMS Entertainment character design',
      'A-1 Pictures gothic anime',
      'Studio Bones Vanitas aesthetic',
      'Maru-pen fine linework',
      'delicate precise mapping pen strokes',
      'G-pen outline variation',
      'layered complexity depth rendering',
      'thick outline thin internal detail contrast',
      'bishōnen elegant proportions',
      '5.7-6.5 head toushin proportions',
      'elongated limb rendering',
      'CLAMP noodle people influence',
      'heavy screentone layering',
      'multiple tonal layer shadow patterns',
      'gradient screentone atmospheric depth',
      '50-60 LPI manga frequency',
      'scratching technique starburst effects',
      'beta-nuri solid black contrast',
      'white ink highlight over black',
      'flowing S-curve C-curve hair rendering',
      'section-based strand grouping',
      'individual strand directional shading',
      'large eyes multiple light reflection points',
      'detailed iris patterns',
      'Year 24 Group female manga legacy',
      'aesthetic yet dynamic superbly beautiful rendering',
      '(d.gray-man_(style):1.2)',
      '(hoshino_katsura_(style):1.3)',
      '(toboso_yana_(style):1.2)',
      'black_butler_(style)',
      'gothic',
      'masterpiece'
    ]
  },
  {
    id: 'melancholic_softness',
    name: 'Melancholic',
    description: 'Melancholic 90s anime style, vulnerable characters, watercolor-adjacent, subdued eyes',
    previewColor: 'linear-gradient(135deg, #483D8B 0%, #FF4500 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/melancholic.jpg',
    promptKeywords: [
      'Yoshiyuki Sadamoto art style',
      'Evangelion character design',
      'Gainax 1990s aesthetic',
      'FLCL visual style',
      'melancholic anime aesthetic',
      'soft-edge rendering technique',
      'watercolor-adjacent coloring',
      'emotional vulnerability design',
      'large subdued eye shine',
      'muted eye highlights',
      'detailed hair strand technique',
      'tentative delicate linework',
      'psychological depth rendering',
      'fragile character aesthetic',
      'soft pastel undertones',
      'Der Mond artbook style',
      '1990s Gainax aesthetic',
      'Khara studio influence',
      'vulnerable protagonist design',
      'introspective melancholy mood',
      'dissolution-prone rendering',
      'gentle sad atmosphere',
      'nostalgic soft focus',
      'emotional weight illustration',
      'soft realistic proportions',
      '(neon_genesis_evangelion_(style):1.3)',
      '(sadamoto_yoshiyuki_(style):1.4)',
      'watercolor_(style)',
      'melancholic'
    ]
  },
  {
    id: 'brushwork',
    name: 'Brushwork',
    description: 'Calligraphic ink technique, sumi-e wash, visible brush strokes',
    previewColor: 'linear-gradient(135deg, #2F4F4F 0%, #8B4513 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/brushwork.jpg',
    promptKeywords: [
      'Takehiko Inoue art style',
      'Vagabond manga aesthetic',
      'sumi-e ink wash style',
      'Japanese calligraphic brushwork',
      'traditional ink painting manga',
      'brush stroke variation',
      'visible pressure modulation',
      'wet-on-wet ink technique',
      'sumi ink wash gradients',
      'economy of lines principle',
      'missing edges technique',
      'implied form rendering',
      'Kuretake brush pen style',
      'expressive calligraphic lines',
      'ink wash value gradation',
      'negative space mastery',
      'samurai ink illustration',
      'traditional Japanese brush technique',
      'individual hair strand brushwork',
      'atmospheric ink wash',
      'minimalist stroke efficiency',
      'brush pressure emotion',
      'wet brush soft edges',
      'dry brush texture',
      'gestural confident strokes',
      'meditative ink rendering',
      'SUMI artbook aesthetic',
      'classical Japanese illustration influence',
      '(vagabond_(style):1.3)',
      '(inoue_takehiko_(style):1.4)',
      '(sumi-e:1.2)',
      'ink_wash_painting',
      'brush_stroke',
      'masterpiece'
    ]
  },
  {
    id: 'technical_hardsurface',
    name: 'Technical',
    description: '80s cyberpunk style, engineered precision, mechanical crosshatching',
    previewColor: 'linear-gradient(135deg, #708090 0%, #2F4F4F 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/technical.jpg',
    promptKeywords: [
      'Katsuhiro Otomo art style',
      'Akira manga aesthetic',
      'Neo-Tokyo illustration',
      'Masamune Shirow style',
      'Ghost in the Shell manga',
      '1980s cyberpunk manga',
      'technical illustration manga',
      'Production I.G aesthetic',
      'precise mechanical rendering',
      'hard-edge shading technique',
      'clean terminator shadow lines',
      'technical pen linework',
      'mechanical crosshatching',
      'architectural precision drawing',
      'biomechanical integration',
      'detailed machinery rendering',
      'realistic human proportions',
      'unexaggerated facial features',
      'structural hair rendering',
      'engineered character design',
      'system diagram aesthetic',
      'perspective grid precision',
      'urban destruction detail',
      'technical blueprint influence',
      'hard science fiction manga',
      'dense environmental detail',
      'Intron Depot aesthetic',
      '(shirow_masamune_(style):1.4)',
      '(ghost_in_the_shell_(style):1.3)',
      'cyberpunk',
      'mecha_musume',
      'masterpiece'
    ]
  },
  {
    id: 'seinen_asano',
    name: 'Urban Melancholy',
    description: 'Urban melancholy, hybrid analog-digital, photo-processed backgrounds, gray tone emphasis',
    previewColor: 'linear-gradient(135deg, #696969 0%, #A9A9A9 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/asano_school.jpg',
    promptKeywords: [
      'Inio Asano art style',
      'Goodnight Punpun manga aesthetic',
      'Big Comic Spirits seinen style',
      'urban melancholy manga movement',
      'G-pen fine-tipped nib linework',
      'standard brush pen technique',
      'ballpoint pen casual marks',
      'hybrid analog-digital workflow',
      'hand-drawn character rendering',
      'photo-processed background technique',
      'Photoshop binarization line art',
      '3D environment rendering',
      'digital screentone preference',
      'gray tone printing quality focus',
      'cinematic unusual angle composition',
      'jarring simplistic protagonist contrast',
      'detailed realistic world rendering',
      'alienation visual device',
      'same plane as reality philosophy',
      '(oyasumi_punpun_(style):1.3)',
      '(asano_inio_(style):1.4)',
      'realistic'
    ]
  },
  {
    id: 'josei_historical',
    name: 'Period Drama',
    description: 'Period accuracy obsession, kabura spoon pen, extensive crosshatching, mahaku white space',
    previewColor: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/historical_elegance.jpg',
    promptKeywords: [
      'Fumi Yoshinaga art style',
      'Kaoru Mori illustration',
      'Harta magazine style',
      'Comic Beam aesthetic',
      'kabura spoon pen technique',
      'powerful flexible line quality',
      'highly detailed meticulous linework',
      '7-8 head toushin realistic proportions',
      'revelatory screentone mastery',
      'traditional stick-on screentone',
      'extensive crosshatching texture',
      'mahaku white space technique',
      '(yoshinaga_fumi_(style):1.3)',
      '(mori_kaoru_(style):1.3)',
      'emma_(manga)',
      'historical'
    ]
  },
  {
    id: 'shojo_historical',
    name: 'Fantasy Ornate',
    description: 'Dense detailed rendering, heavy G-pen variation, complex screentone layering, costume obsession',
    previewColor: 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/historical_fantasy.jpg',
    promptKeywords: [
      'Yuu Watase art style',
      'Yumi Tamura illustration',
      'Kairi Yura manga aesthetic',
      'Shōjo Comic historical fantasy style',
      'Studio Pierrot fantasy adaptation',
      'Madhouse period drama aesthetic',
      'G-pen dramatic thick-to-thin variation',
      'traditional colored ink technique',
      'marker and brushwork rendering',
      'heavy line weight variation',
      '7-9 head toushin elegant proportions',
      'heavy screentone reliance',
      'complex layered dimensional toning',
      'physical adhesive screentone technique',
      'kakeami shadow crosshatching',
      'dramatic solid black contrast',
      'white line detail within black areas',
      'India ink filled sheen technique',
      '1990s historical fantasy peak era',
      'old-school fantasy rendering',
      'period detail obsession',
      '(fushigi_yuugi_(style):1.2)',
      '(watase_yuu_(style):1.3)',
      'fantasy'
    ]
  },
  {
    id: 'seinen_taniguchi',
    name: 'Contemplative',
    description: 'Contemplative naturalism, ligne claire clean lines, white space mastery',
    previewColor: 'linear-gradient(135deg, #87CEEB 0%, #F5F5DC 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/taniguchi_school.jpg',
    promptKeywords: [
      'Jiro Taniguchi art style',
      'contemplative manga aesthetic',
      'Big Comic seinen style',
      'La Nouvelle Manga movement',
      'Franco-Japanese artistic exchange',
      'G-pen fine-tipped nib technique',
      'ligne claire clear line style',
      'clean refined pen strokes',
      'strong lines minimal hatching',
      'strict economy of expression',
      '7-8 head toushin realistic proportions',
      'restrained flat screentone',
      'no crosshatching ligne claire tradition',
      'great white space usage',
      'poetic black gray white contrast',
      'Moebius direct collaboration influence',
      'Yasujiro Ozu film rhythm',
      'scenery storytelling approach',
      'gloriously rendered environments',
      'entirely traditional workflow',
      '(taniguchi_jiro_(style):1.4)',
      'kodoku_no_gourmet',
      'seinen',
      'realistic'
    ]
  },
  {
    id: 'retro_delinquent_shonen',
    name: 'Retro Muscular',
    description: '80s muscular manga style, bold linework, dense crosshatching',
    previewColor: 'linear-gradient(135deg, #8B0000 0%, #2F2F2F 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/retro_delinquent_shonen.jpg',
    promptKeywords: [
      'Tetsuo Hara art style',
      'Fist of the North Star manga aesthetic',
      'Hirohiko Araki early JoJo illustration',
      'JoJo Parts 1-3 visual style',
      'Hiroshi Takahashi Crows rendering',
      'Masami Kurumada Saint Seiya design',
      'Toei Animation 1980s production',
      'David Production faithful adaptation',
      'Gekiga Sonjuku Kazuo Koike training',
      'G-pen bold dramatic linework',
      'thick-to-thin dramatic transitions',
      'layered pen stroke intensity buildup',
      'Saji Spoon pen controlled lines',
      'Maru Mapping pen fine hair details',
      '0.5-1.5mm bold confident outlines',
      'heavy beta solid black brush fills',
      'cross-hatching shadow musculature indication',
      'multiple direction darker value hatching',
      'curved hatching following muscle contours',
      'dense screentone adhesive sheet application',
      '50-60 LPI standard frequency',
      '8 head toushin classical heroic proportions',
      'Michelangelo David Renaissance sculpture influence',
      'Frank Frazetta fantasy illustration influence',
      'Mad Max 2 Blade Runner aesthetic',
      'sharp angular hair shapes',
      'heavy black mass white highlight streaks',
      'volume as sculptural hair mass',
      'bold outline hair silhouette definition',
      'detailed iris pupil rendering',
      'gekiga sharp angle hatching gritty lines',
      '1983-1995 peak era aesthetic',
      'traditional cel animation genga douga process',
      'OVA era high production values',
      '(hokuto_no_ken_(style):1.3)',
      '(hara_tetsuo_(style):1.4)',
      'muscular',
      'retro_art_style',
      'masterpiece'
    ]
  },
  {
    id: 'sketchy_chaotic',
    name: 'Sketchy',
    description: 'Deliberately unfinished lines, raw energy, ink splatter texture',
    previewColor: 'linear-gradient(135deg, #1a1a1a 0%, #8B0000 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/sketchy.jpg',
    promptKeywords: [
      'Gege Akutami manga style',
      'Jujutsu Kaisen aesthetic',
      'Tatsuki Fujimoto art style',
      'Chainsaw Man manga art',
      'rough sketchy linework',
      'intentionally unfinished lines',
      'visible construction underdrawing',
      'aggressive hatching technique',
      'thick loose linework',
      'kinetic line energy',
      'ink splatter texture',
      'raw energetic strokes',
      'psychological tension rendering',
      'stark contrast rendering',
      'sparse detailed background contrast',
      'rapid sketch aesthetic',
      'unrefined brush strokes',
      '2020s Shonen Jump style',
      'MAPPA adaptation source',
      'deliberate roughness',
      'frantic handwritten quality',
      'emotional raw intensity',
      'scratchy textured lines',
      'contemporary battle manga',
      'dark seinen-adjacent shonen',
      'visceral impact rendering',
      '(fujimoto_tatsuki_(style):1.4)',
      '(chainsaw_man_(style):1.3)',
      'sketch',
      'rough_lines'
    ]
  },
  {
    id: 'ukiyoe_revival',
    name: 'Woodblock Print',
    description: 'Japanese woodblock print style, dramatic supernatural aesthetic, bold outlines',
    previewColor: 'linear-gradient(135deg, #DC143C 0%, #FFD700 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/woodblock.jpg',
    promptKeywords: [
      'traditional ukiyo-e woodblock print',
      'Katsushika Hokusai woodcut',
      'Utagawa Kuniyoshi print style',
      'Kitagawa Utamaro bijin-ga',
      'Japanese woodblock printing technique',
      'hand-carved woodcut line quality',
      'completely flat color areas NO gradients',
      'solid color fills with hard edges',
      'bold black keyblock outlines',
      'traditional Japanese color palette',
      'vermillion red indigo blue ochre yellow',
      'visible woodgrain texture in lines',
      'washi rice paper texture',
      'limited 5-7 color palette maximum',
      'color separation print technique',
      'stylized simplified features',
      'decorative flowing linework',
      'Edo period artistic aesthetic',
      'nishiki-e multicolor woodblock',
      'theatrical kabuki influenced',
      'supernatural dramatic atmosphere',
      '(ukiyo-e:1.3)',
      'traditional_media',
      'japanese_art',
      '(hokusai_katsushika_(style):1.2)'
    ]
  },
  {
    id: 'tatsunoko_retro',
    name: 'Retro Sci-Fi',
    description: '70s retro anime style, aerodynamic design, retro-futuristic aesthetic',
    previewColor: 'linear-gradient(135deg, #FF6347 0%, #4682B4 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/tatsunoko_retro.jpg',
    promptKeywords: [
      'Tatsunoko Production style',
      'Speed Racer aesthetic',
      'Gatchaman art style',
      '1970s anime illustration',
      'Showa era anime design',
      'retro-futuristic anime',
      'Science Ninja Team aesthetic',
      'atomic age character design',
      'geometric angular rendering',
      'molded plastic body smoothness',
      'aerodynamic character design',
      'automotive design influence',
      'aerospace sleek aesthetic',
      'bold primary colors',
      'high saturation limited palette',
      'American comic influence',
      'streamlined design',
      'optimistic futurism rendering',
      'clean angular planes',
      'vintage mecha anime',
      'Battle of the Planets style',
      'classic Japanese superhero design',
      '(tatsunoko_production:1.3)',
      'gatchaman_(style)',
      'retro_art_style',
      '1970s_(style)'
    ]
  },
  {
    id: 'josei_naturalism',
    name: 'Modern Josei',
    description: 'Design background elegance, thin clean elongated lines, complex collage layouts',
    previewColor: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/adult_naturalism.jpg',
    promptKeywords: [
      'Mari Okazaki art style',
      'Akiko Higashimura illustration',
      'Feel Young magazine josei style',
      'thin clean elongated line quality',
      'sketchy expressive wispy strokes',
      'refined expressive linework',
      'minimal delicate screentoning',
      'bleed collage-like layer composition',
      '(okazaki_mari_(style):1.3)',
      'josei',
      'supple'
    ]
  },
  {
    id: 'josei_dark',
    name: 'Dark Drama',
    description: 'Psychological intensity, harsh black-white contrast, loose reckless penwork',
    previewColor: 'linear-gradient(135deg, #2F2F2F 0%, #696969 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/josei_dark.jpg',
    promptKeywords: [
      'Kyoko Okazaki art style',
      'Shuzo Oshimi illustration',
      'Helter Skelter manga aesthetic',
      'Feel Young dark josei style',
      'harsh contrast rendering',
      'negative space magnification',
      'loose reckless penwork abandon',
      'minimalist harsh approach',
      'Patrick Nagel 80s influence',
      'realistic raw emotion rendering',
      'exceptional facial expression conveyance',
      'exaggerated frame fashion caricature',
      'heroin chic 1990s aesthetic',
      'heavy solid black usage',
      'harsh contrast negative space composition',
      'direct tonal opposition',
      'desolation perception conflict rendering',
      '1990s-2000s dark josei era',
      '(okazaki_kyoko_(style):1.4)',
      'helter_skelter_(style)',
      'josei',
      'high_contrast'
    ]
  },
  {
    id: 'josei_gourmet',
    name: 'Slice of Life',
    description: 'Food rendering obsession, multi-register caricature-to-photorealistic, 4-tone system',
    previewColor: 'linear-gradient(135deg, #FF8C00 0%, #FFD700 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/gourmet_lifestyle.jpg',
    promptKeywords: [
      'Fumi Yoshinaga art style',
      'Shu Okimoto illustration',
      'Morning magazine josei style',
      'Big Comic Spirits manga',
      'clean crisp detailed linework',
      'recognizable emotional figure rendering',
      'multi-register caricature-photorealistic approach',
      '4-tone shading system',
      'solid black dark shadow light shadow white',
      'crisp outline technique',
      'wordless emotional rendering',
      'oishinbo',
      'seinen'
    ]
  },
  {
    id: 'pre_tezuka',
    name: 'Pre-Tezuka',
    description: 'Rubber-hose 1930s style, pie-cut eyes, bold outlines, theatrical simplicity',
    previewColor: 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/pre-tezuka.jpg',
    promptKeywords: [
      'rubber hose animation style',
      '1930s Japanese animation',
      'pre-war anime aesthetic',
      'Fleischer Brothers influence',
      'kamishibai illustration style',
      'pie-eyed character design',
      'circular dot pupils',
      'noodle limbs',
      'tube-like bendable arms',
      'squash and stretch deformation',
      'bold black outlines',
      'high-contrast black and white',
      'simplified circular forms',
      'theatrical flat rendering',
      'cutout animation aesthetic',
      'silhouette-friendly design',
      'jointless flowing limbs',
      'oval head shapes',
      'mitten hands',
      'simple geometric body construction',
      'vintage cartoon proportions',
      'stark value contrast',
      'minimal interior detail',
      'mask-like facial features',
      'retro_art_style',
      '1930s_(style)',
      '(rubber_hose:1.2)',
      'monochrome'
    ]
  },
  {
    id: 'seinen_one',
    name: 'Webcomic Raw',
    description: 'Minimalist anti-style, deliberate crudeness, extreme detail contrast for impact',
    previewColor: 'linear-gradient(135deg, #D3D3D3 0%, #808080 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/one_school.jpg',
    promptKeywords: [
      'ONE art style',
      'Taiyo Matsumoto illustration',
      'webcomic crude aesthetic',
      'anti-style manga movement',
      'Monthly Ikki experimental aesthetic',
      'middle school sketchbook quality',
      'crude wobbly deliberate lines',
      'messy aggressive mark-making',
      'arasa roughness aesthetic',
      'highly distorted toushin ratios',
      'simplified proportion systems',
      'minimal to no screentone',
      'black emotional impact usage',
      'extreme detail contrast technique',
      'minimalism to extreme detail flipping',
      'stiff unconventional character appeal',
      'skewed angle contorted composition',
      'psychological realism through form',
      'intentional crudeness philosophy',
      'artlessness complementing absurdity',
      '(one_punch_man_(one):1.2)',
      '(mob_psycho_100_(style):1.2)',
      '(rough_lines:1.2)',
      'scribble'
    ]
  },
];

// ============================================
// PHOTOREALISTIC STYLES
// ============================================

const PHOTOREALISTIC_STYLES: StyleOption[] = [
  {
    id: 'hyperrealism',
    name: 'Hyperrealism',
    description: 'Microscopic details, skin pores, imperfections, extreme clarity',
    previewColor: 'linear-gradient(135deg, #DEB887 0%, #D2B48C 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/hyperrealism.jpg',
    promptKeywords: [
      'hyperrealism style',
      'hyperrealistic photograph',
      '8K UHD resolution',
      'visible skin pores and texture',
      'micro detail capture',
      'razor sharp focus',
      'photorealistic skin rendering',
      'medium format camera quality',
      'f/2.8 shallow depth of field',
      'color accurate rendering'
    ]
  },
  {
    id: 'contemporary_realism',
    name: 'Contemporary Realism',
    description: 'Direct observation, controlled brushstrokes, texture suggestion',
    previewColor: 'linear-gradient(135deg, #D2691E 0%, #8B4513 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/contemporary_realism.jpg',
    promptKeywords: [
      'contemporary realism style',
      'contemporary realism painting',
      '1990s-2020s atelier training',
      'representational art genre',
      'painted by Richard Schmid',
      'in the style of Jeremy Lipking',
      'in the style of Daniel Gerhartz',
      'direct observation technique',
      'visible controlled brushstrokes',
      'Alla Prima wet-on-wet technique',
      'naturalistic color palette',
      'traditional oil painting medium',
      'accurate value structure'
    ]
  },
  {
    id: 'engraving',
    name: 'Engraving',
    description: 'High-precision lines, varying density, currency/certificate look',
    previewColor: 'linear-gradient(135deg, #2E8B57 0%, #F0F8FF 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/engraving.jpg',
    promptKeywords: [
      'engraving style',
      'steel engraving art',
      '18th-19th century printmaking',
      'intaglio technique',
      'banknote currency aesthetic',
      'in the style of Albrecht Dürer',
      'cross-hatching density shading',
      'fine line precision',
      'antique certificate style',
      'money portrait aesthetic',
      'official document art'
    ]
  },
  {
    id: 'subsurface_scatter',
    name: 'Subsurface Study',
    description: 'Translucent skin rendering, glowing skin quality, porcelain texture',
    previewColor: 'linear-gradient(135deg, #FFDEAD 0%, #FFA07A 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/subsurface_scatter.jpg',
    promptKeywords: [
      'subsurface scattering skin rendering',
      'translucent material quality',
      'organic material properties',
      'translucent skin tone',
      'jade or porcelain skin quality',
      'ray-traced subsurface scattering'
    ]
  },
  {
    id: 'trompe_loeil',
    name: 'Trompe-l\'œil',
    description: 'Forced perspective, precise shadowing, 3D illusion on flat surface',
    previewColor: 'linear-gradient(135deg, #BC8F8F 0%, #8B4513 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/trompe_loeil.jpg',
    promptKeywords: [
      'trompe-l\'œil painting style',
      'hyper-realistic oil painting',
      'Pere Borrell del Caso influence',
      'photorealistic painted portrait',
      'museum quality fine art',
      'classical oil painting technique',
      'flat background painting'
    ]
  },
  {
    id: 'portrait_realism',
    name: 'Portrait',
    description: 'Subsurface scattering skin, detailed iris patterns, studio lighting',
    previewColor: 'linear-gradient(135deg, #F5DEB3 0%, #DEB887 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/portrait_realism.jpg',
    promptKeywords: [
      'portrait photography style',
      'professional portrait photography',
      '2000s-2020s studio photography',
      'fashion portrait genre',
      'shot by Peter Lindbergh',
      'in the style of Annie Leibovitz',
      'in the style of Richard Avedon',
      'high-end retouching',
      '85mm portrait lens',
      'professional studio backdrop'
    ]
  },
  {
    id: 'macro_realism',
    name: 'Macro Detail',
    description: 'Extreme detail, microscopic textures revealed, scientific precision',
    previewColor: 'linear-gradient(135deg, #90EE90 0%, #32CD32 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/macro_realism.jpg',
    promptKeywords: [
      'macro detail photography style',
      'microscopic detail revealed',
      'texture emphasis rendering',
      'scientific specimen detail',
      'extreme surface detail',
      'macro lens 1:1 magnification',
      'focus stacking technique',
      'clinical sharp focus'
    ]
  },
  {
    id: 'cinematic',
    name: 'Cinematic',
    description: 'Movie still aesthetic, teal and orange color grading, filmic quality',
    previewColor: 'linear-gradient(135deg, #2F4F4F 0%, #1A1A2E 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/cinematic.jpg',
    promptKeywords: [
      'cinematic film still',
      'teal and orange color grade',
      'movie production still',
      'filmic color rendering',
      'cinematic color grading',
      '35mm film grain texture',
      'shallow depth of field bokeh',
      'ARRI Alexa camera look'
    ]
  },
  {
    id: 'fashion',
    name: 'Fashion',
    description: 'High-end editorial look, professional retouching, glossy quality',
    previewColor: 'linear-gradient(135deg, #FFE4E1 0%, #FFC0CB 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/fashion.jpg',
    promptKeywords: [
      'high fashion editorial photograph',
      'Vogue magazine cover style',
      'professional beauty retouching',
      'glossy magazine quality',
      'high-end fashion photography aesthetic',
      'catch light in eyes',
      'frequency separation retouching',
      'magazine print quality',
      'Phase One medium format look'
    ]
  },
  {
    id: 'landscape_realism',
    name: 'Natural Realism',
    description: 'Natural skin tones, organic textures, earthly color palette',
    previewColor: 'linear-gradient(135deg, #87CEEB 0%, #4169E1 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/landscape_realism.jpg',
    promptKeywords: [
      'natural realism photography',
      'National Geographic portrait style',
      'organic natural skin tones',
      'earthly color palette',
      'environmental portrait style',
      'available light photography',
      'documentary photography aesthetic'
    ]
  },
  {
    id: 'industrial',
    name: 'Industrial',
    description: 'Gritty aesthetic, matte finishes, desaturated palette',
    previewColor: 'linear-gradient(135deg, #696969 0%, #2F2F2F 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/industrial.jpg',
    promptKeywords: [
      'industrial photography aesthetic',
      'gritty matte surface rendering',
      'desaturated color palette',
      'working class documentary style',
      'raw unpolished aesthetic',
      'high ISO grain texture',
      'low contrast flat profile'
    ]
  },
  {
    id: 'luminism',
    name: 'Luminism',
    description: 'Hidden brushstrokes, glowing quality, ethereal painting style',
    previewColor: 'linear-gradient(135deg, #FFFACD 0%, #FFD700 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/luminism.jpg',
    promptKeywords: [
      'luminism painting style',
      'ethereal glowing skin quality',
      'romantic realism painting',
      'smooth blended brushstrokes',
      'warm golden color palette'
    ]
  }
];

// ============================================
// 3D GAME STYLES
// ============================================

const GAME_3D_STYLES: StyleOption[] = [
  {
    id: 'pbr',
    name: 'PBR Realistic',
    description: 'Physically based rendering, realistic material properties, AAA quality',
    previewColor: 'linear-gradient(135deg, #708090 0%, #2F4F4F 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/pbr.jpg',
    promptKeywords: [
      'PBR style',
      'physically based rendering',
      '2010s-2020s AAA game graphics',
      'realistic game character genre',
      'rendered in Unreal Engine 5',
      'rendered in Unity HDRP',
      'art style similar to The Last of Us Part II',
      'realistic material properties',
      'ray-traced global illumination',
      'metallic roughness workflow',
      'photogrammetry textures',
      'next-gen game graphics',
      'subsurface scattering skin shader',
      '4K texture resolution',
      'screen space reflections'
    ]
  },
  {
    id: 'low_poly',
    name: 'Low Poly',
    description: 'Visible sharp-edged triangles, folded paper aesthetic, minimal detail',
    previewColor: 'linear-gradient(135deg, #00CED1 0%, #20B2AA 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/low_poly.jpg',
    promptKeywords: [
      'low poly style',
      'low poly 3D model',
      '2010s indie game aesthetic',
      'geometric minimalist art',
      'art style similar to Monument Valley',
      'art style similar to Superhot',
      'geometric faceted surfaces',
      'flat shaded polygons',
      'minimalist 3D art',
      'origami paper craft look',
      'clean geometric shapes',
      'limited polycount design',
      'hard edge normals',
      'vertex color shading',
      'unsmoothed mesh topology'
    ]
  },
  {
    id: 'cel_shaded',
    name: 'Cel-Shaded',
    description: 'Flat stepped shading, black silhouette outlines, toon rendering',
    previewColor: 'linear-gradient(135deg, #FF6347 0%, #FF4500 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/cel_shaded.jpg',
    promptKeywords: [
      'cel-shaded style',
      'cel-shaded toon rendering',
      '2000s-2020s stylized 3D',
      'cartoon 3D genre',
      'art style similar to Borderlands',
      'art style similar to Jet Set Radio',
      'art style similar to Guilty Gear Strive',
      'anime-style 3D graphics',
      'black ink outline edges',
      'flat color step shading',
      'two-tone shadow technique',
      'NPR non-photorealistic rendering',
      'toon shader material',
      'rim light edge detection',
      'posterized color ramp'
    ]
  },
  {
    id: 'voxel',
    name: 'Voxel',
    description: 'Uniform cubes, grid-based aesthetic, digital LEGO look',
    previewColor: 'linear-gradient(135deg, #32CD32 0%, #228B22 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/voxel.jpg',
    promptKeywords: [
      'voxel art style',
      'voxel 3D art',
      '2010s voxel game aesthetic',
      'blocky 3D genre',
      'art style similar to Minecraft',
      'art style similar to Crossy Road',
      'rendered in MagicaVoxel',
      'cube-based character model',
      'blocky pixel art 3D',
      'isometric voxel rendering',
      'cubic grid structure',
      'volumetric pixels',
      'ambient occlusion voxel shading'
    ]
  },
  {
    id: 'hand_painted',
    name: 'Hand-Painted',
    description: 'Visible brushstrokes, baked lighting, painterly textures',
    previewColor: 'linear-gradient(135deg, #DDA0DD 0%, #BA55D3 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/hand_painted.jpg',
    promptKeywords: [
      'hand-painted 3D style',
      'hand-painted 3D textures',
      '2000s-2020s stylized game art',
      'fantasy game character genre',
      'art style similar to World of Warcraft',
      'art style similar to Hearthstone',
      'art style similar to League of Legends',
      'by Blizzard Entertainment',
      'painterly diffuse maps',
      'visible brush strokes texture',
      'baked ambient occlusion',
      'warm color temperature',
      'hand-painted normal maps',
      'stylized specular highlights',
      'painted shadow gradients'
    ]
  },
  {
    id: 'stylized_realism',
    name: 'Stylized Realism',
    description: 'Realistic lighting with exaggerated cartoon proportions, idealized smooth surfaces',
    previewColor: 'linear-gradient(135deg, #87CEFA 0%, #4169E1 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/stylized_realism.jpg',
    promptKeywords: [
      'stylized realism 3D',
      'Disney Pixar character style',
      'Fortnite character design',
      'appealing cartoon proportions',
      'idealized features',
      'smooth subsurface skin',
      'Overwatch hero design',
      'exaggerated silhouette',
      'dreamworks animation style',
      'Ratchet and Clank aesthetic',
      'family-friendly 3D character',
      'Arnold renderer quality',
      'softened edge bevels',
      'smooth subdivision surfaces'
    ]
  },
  {
    id: 'retro_ps1',
    name: 'Retro PS1',
    description: 'Texture warping, crunchy low-res textures, nostalgic artifacts',
    previewColor: 'linear-gradient(135deg, #9370DB 0%, #483D8B 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/retro_ps1.jpg',
    promptKeywords: [
      'PS1 PlayStation 1 graphics',
      'retro low poly 3D',
      'texture warping affine',
      'crunchy 256x256 textures',
      'vertex jitter wobble',
      'limited color palette',
      'dithered gradients',
      'early 3D game aesthetic',
      'Silent Hill PS1 style',
      'nostalgic 32-bit graphics',
      'no texture filtering',
      'fixed-point precision artifacts',
      'gouraud shading'
    ]
  },
  {
    id: 'fantasy_realism',
    name: 'Fantasy Realism',
    description: 'Desaturated gritty palette, detailed textures, dark fantasy aesthetic',
    previewColor: 'linear-gradient(135deg, #8B4513 0%, #2F1F0F 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/fantasy_realism.jpg',
    promptKeywords: [
      'dark fantasy 3D art style',
      'Souls-like visual aesthetic',
      'Elden Ring rendering style',
      'desaturated muted color palette',
      'FromSoftware art direction',
      'grimdark fantasy aesthetic',
      'realistic skin rendering',
      'high detail normal maps',
      'weathered material textures',
      'volumetric fog atmosphere'
    ]
  },
  {
    id: 'flat_shaded',
    name: 'Flat Shaded',
    description: 'No gradients or textures, solid color blocks, silhouette emphasis',
    previewColor: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/flat_shaded.jpg',
    promptKeywords: [
      'flat shaded 3D render style',
      'solid color no texture',
      'unlit material rendering',
      'silhouette focused design',
      'minimalist 3D art',
      'vector-like 3D graphics',
      'single color surfaces',
      'Superhot game visual style',
      'untextured clean model',
      'stark color blocking'
    ]
  },
  {
    id: 'plushcore',
    name: 'Plushcore',
    description: 'Physical fabrics, felt textures, visible seams, soft toy aesthetic',
    previewColor: 'linear-gradient(135deg, #FFB6C1 0%, #87CEFA 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/plushcore.jpg',
    promptKeywords: [
      'knitted wool texture',
      'felt fabric material 3D',
      'visible sewing seams',
      'soft plush toy aesthetic',
      'Yoshi\'s Woolly World style',
      'fabric fuzz detail',
      'button eyes',
      'stitching thread details',
      'cloth simulation',
      'LittleBigPlanet textures',
      'handmade craft look'
    ]
  },
  {
    id: 'hybrid_realism',
    name: 'Hybrid Realism',
    description: 'Stylized expressive characters with realistic skin rendering',
    previewColor: 'linear-gradient(135deg, #FF7F50 0%, #00CED1 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/hybrid_realism.jpg',
    promptKeywords: [
      'hybrid realism art style',
      'Final Fantasy VII Remake style',
      'expressive anime face realistic skin',
      'PBR textures stylized proportions',
      'Kingdom Hearts 4 aesthetic',
      'detailed material rendering',
      'high fidelity stylized',
      'modern JRPG graphics',
      'Luminous Engine quality',
      'hair strand rendering',
      'realistic eye shader'
    ]
  }
];

// ============================================
// COMIC STYLES
// ============================================

const COMIC_STYLES: StyleOption[] = [
  // Modern Superhero is DEFAULT for comic/cartoon
  {
    id: 'modern_superhero',
    name: 'Modern Superhero',
    description: 'Digital painting over ink, cinematic gradients, detailed rendering',
    previewColor: 'linear-gradient(135deg, #4169E1 0%, #1E90FF 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/modern_superhero.jpg',
    promptKeywords: [
      'modern superhero comic art',
      'Jim Lee X-Men style',
      'digital color over ink',
      'Marvel NOW aesthetic',
      'Alex Ross painted influence',
      'crosshatch muscle rendering',
      'contemporary DC Marvel style'
    ]
  },
  {
    id: 'bronze_age',
    name: 'Bronze Age',
    description: 'Gritty realism, heavy cross-hatching, defined musculature',
    previewColor: 'linear-gradient(135deg, #CD853F 0%, #8B4513 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/bronze_age.jpg',
    promptKeywords: [
      'bronze age comic style',
      'bronze age comic art',
      '1970s-1980s American comics',
      'art style similar to Batman (1970s)',
      'in the style of Neal Adams',
      'heavy cross-hatching shadows',
      'gritty realism illustration',
      'photorealistic comic coloring'
    ]
  },
  {
    id: 'scratchboard',
    name: 'Scratchboard',
    description: 'Subtractive white-on-black, jagged textures, high contrast woodcut look',
    previewColor: 'linear-gradient(135deg, #000000 0%, #FFFFFF 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/scratchboard.jpg',
    promptKeywords: [
      'scratchboard illustration technique',
      'scratch board art medium',
      'subtractive white on black drawing',
      'noir graphic novel genre',
      'in the style of Thomas Ott',
      'white scratched lines on black surface',
      'woodcut print texture',
      'expressive scratched linework',
      'high contrast noir aesthetic',
      'traditional scratchboard medium'
    ]
  },
  {
    id: 'propaganda_wpa',
    name: 'WPA / Propaganda',
    description: 'Bold geometric shapes, strong silhouettes, limited palette, powerful forms',
    previewColor: 'linear-gradient(135deg, #B22222 0%, #2F4F4F 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/propaganda_wpa.jpg',
    promptKeywords: [
      'WPA propaganda poster style',
      'WPA poster art aesthetic',
      '1930s-1940s propaganda posters',
      'constructivist design movement',
      'Soviet propaganda poster aesthetic',
      'in the style of Shepard Fairey',
      'bold geometric silhouettes',
      'limited color palette',
      'strong vector forms',
      'block print texture',
      'heroic stylized rendering'
    ]
  },
  {
    id: 'pop_art',
    name: 'Pop Art',
    description: 'Kirby Crackle dots, explosive energy, bold primary colors, dynamic motion',
    previewColor: 'linear-gradient(135deg, #FF0000 0%, #FFFF00 100%)',
    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/pop_art.jpg',
    promptKeywords: [
      'pop art style portrait',
      'pop art comic illustration',
      '1960s pop art movement',
      'in the style of Roy Lichtenstein',
      'bold primary colors',
      'benday dots halftone print',
      'thick black outlines',
      'retro 60s comic aesthetic',
      'clean pop art rendering'
    ]
  },
  {
    id: 'golden_age',
    name: 'Golden Age',
    description: 'Thick uniform lines, flat coloring, heroic simplified muscles',
    previewColor: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/golden_age.jpg',
    promptKeywords: [
      'golden age comic book art',
      '1940s superhero illustration style',
      'Jack Kirby style',
      'thick uniform ink lines',
      'Ben-Day dot printing',
      'CMYK limited palette',
      'idealized features',
      'pulp adventure aesthetic',
      'vintage comic book art',
      'clean comic illustration'
    ]
  },
  {
    id: 'ligne_claire',
    name: 'Ligne Claire',
    description: 'Constant-width outlines, no hatching, equally detailed world and characters',
    previewColor: 'linear-gradient(135deg, #F0F8FF 0%, #E6E6FA 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/ligne_claire.jpg',
    promptKeywords: [
      'ligne claire clear line style',
      'Herge Tintin aesthetic',
      'uniform line weight',
      'no cross-hatching',
      'flat color areas',
      'Belgian comic tradition',
      'Moebius clean linework',
      'atomic age design',
      'clear readable artwork',
      'Franco-Belgian bande dessinee'
    ]
  },
  {
    id: 'noir',
    name: 'Noir',
    description: 'Extreme chiaroscuro, deep black ink, high contrast rendering',
    previewColor: 'linear-gradient(135deg, #1A1A1A 0%, #000000 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/noir.jpg',
    promptKeywords: [
      'noir comic book style',
      'Frank Miller Sin City',
      'extreme chiaroscuro contrast',
      'heavy black ink shadows',
      'stark contrast rendering',
      'German expressionist influence',
      'high contrast ink rendering'
    ]
  },
  {
    id: 'silver_age',
    name: 'Silver Age',
    description: 'Kirby Crackle energy, bold outlines, cosmic aesthetic',
    previewColor: 'linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/silver_age.jpg',
    promptKeywords: [
      'silver age Marvel comics style',
      'Jack Kirby comic art',
      'Stan Lee era Marvel aesthetic',
      'Fantastic Four visual style',
      'Steve Ditko illustration influence',
      'bold comic ink outlines',
      'clean silver age rendering'
    ]
  },
  {
    id: 'alternative',
    name: 'Alternative/Indie',
    description: 'Messy linework, varied weights, intentional anatomical distortion',
    previewColor: 'linear-gradient(135deg, #8FBC8F 0%, #556B2F 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/alternative.jpg',
    promptKeywords: [
      'alternative indie comic art',
      'underground comix style',
      'raw expressive linework',
      'intentional distortion',
      'personal artistic voice',
      'Fantagraphics aesthetic',
      'Daniel Clowes influence',
      'autobiographical comics',
      'hand-drawn imperfections',
      'zine DIY aesthetic',
      'Chris Ware design elements'
    ]
  },
  {
    id: 'euro_comic',
    name: 'Euro-Comic',
    description: 'Fine-line backgrounds, watercolor shading, panoramic environmental detail',
    previewColor: 'linear-gradient(135deg, #B0C4DE 0%, #6495ED 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/euro_comic.jpg',
    promptKeywords: [
      'European graphic novel style',
      'Moebius Jean Giraud art',
      'fine detailed linework',
      'watercolor painted colors',
      'bande dessinee illustration',
      'Enki Bilal aesthetic',
      'Metal Hurlant magazine style',
      'Franco-Belgian comic art',
      'clean European comic rendering'
    ]
  },
  {
    id: 'gothic_horror',
    name: 'Gothic Horror',
    description: 'Scratch-board textures, dense stippling, grimy unsettling aesthetic',
    previewColor: 'linear-gradient(135deg, #4A0000 0%, #1A0000 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/gothic_horror.jpg',
    promptKeywords: [
      'gothic horror comic art style',
      'EC Comics horror style',
      'Bernie Wrightson illustration',
      'dense stippling technique',
      'scratch-board texture',
      'Victorian gothic aesthetic',
      'Tales from the Crypt style',
      'Mike Mignola Hellboy style',
      'macabre detailed linework',
      'clean horror portrait rendering'
    ]
  },
  {
    id: 'webtoon',
    name: 'Webtoon',
    description: 'Clean digital look, vibrant colors, soft cel shading',
    previewColor: 'linear-gradient(135deg, #E0FFFF 0%, #AFEEEE 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/webtoon.jpg',
    promptKeywords: [
      'webtoon digital comic style',
      'Korean manhwa aesthetic',
      'clean vector linework',
      'vibrant saturated colors',
      'LINE Webtoon style',
      'soft cel shading',
      'Solo Leveling art style'
    ]
  },
  {
    id: 'sketchy',
    name: 'Sketchy/Gestural',
    description: 'Open unclosed lines, quick gesture drawing energy, mood over polish',
    previewColor: 'linear-gradient(135deg, #D3D3D3 0%, #A9A9A9 100%)',

    previewImage: 'https://pub-f5a158f0e78640529bd030475f5d7491.r2.dev/templates/styles/sketchy.jpg',
    promptKeywords: [
      'sketchy gestural illustration',
      'rough pencil energy',
      'open unclosed linework',
      'expressive quick strokes',
      'Ashley Wood painted style',
      'Bill Sienkiewicz influence',
      'impressionistic comic art',
      'unfinished sketch aesthetic',
      'charcoal smudge texture',
      'raw expressive artwork'
    ]
  }
];

// ============================================
// STYLE MAP BY ARCHETYPE
// ============================================

export const STYLES_BY_ARCHETYPE: Record<CharacterArchetype, StyleOption[]> = {
  manga_anime: ANIME_STYLES,
  film: PHOTOREALISTIC_STYLES,
  game_3d: GAME_3D_STYLES,
  comic_cartoon: COMIC_STYLES
};

/**
 * Get styles for a given archetype
 */
export function getStylesForArchetype(archetype: CharacterArchetype): StyleOption[] {
  return STYLES_BY_ARCHETYPE[archetype] || [];
}

/**
 * Get a specific style by ID
 */
export function getStyleById(archetype: CharacterArchetype, styleId: string): StyleOption | undefined {
  const styles = STYLES_BY_ARCHETYPE[archetype];
  return styles?.find(s => s.id === styleId);
}

/**
 * Get the default/first style for an archetype
 */
export function getDefaultStyleForArchetype(archetype: CharacterArchetype): StyleOption | undefined {
  const styles = STYLES_BY_ARCHETYPE[archetype];
  return styles?.[0];
}
